import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { CompanyDetail } from '@/components/companies/CompanyDetail'
import { DeleteCompanyDialog } from '@/components/companies/DeleteCompanyDialog'
import { useCompany } from '@/hooks/useCompany'
import { useCompanies } from '@/hooks/useCompanies'
import { Button } from '@/components/ui/button'

export default function CompanyDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const { company, loading, error } = useCompany(id || '')
  const { refetchCompanies } = useCompanies()

  const handleEdit = () => {
    navigate(`/companies/${id}/edit`)
  }

  const handleDelete = () => {
    setShowDeleteDialog(true)
  }

  const handleDeleteSuccess = () => {
    setShowDeleteDialog(false)
    // Navigate back to company management after short delay
    setTimeout(() => {
      refetchCompanies()
      navigate('/companies')
    }, 1500)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          <p className="mt-4 text-muted-foreground">Loading company details...</p>
        </div>
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb Navigation */}
          <nav className="mb-4 flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/companies')}
              className="text-primary hover:underline font-medium"
            >
              Companies
            </button>
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground font-medium">
              Not Found
            </span>
          </nav>

          <Button
            variant="outline"
            onClick={() => navigate('/companies')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
          <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">
              Error Loading Company
            </h2>
            <p className="text-destructive">
              {error || 'Company not found'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb Navigation */}
          <nav className="mb-4 flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/companies')}
              className="text-primary hover:underline font-medium"
            >
              Companies
            </button>
            <span className="text-muted-foreground">/</span>
            <span className="text-muted-foreground font-medium">
              {company.name}
            </span>
          </nav>

          {/* Back Button */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/companies')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Companies
            </Button>
          </div>

          {/* Company Detail */}
          <CompanyDetail
            companyId={company.id}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {/* Delete Dialog */}
      {company && (
        <DeleteCompanyDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          companyId={company.id}
          companyName={company.name}
          userCount={company._count?.users || 0}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </>
  )
}
